/**
 * Home's agents: one card each, in a grid that reflows with the width, so
 * nothing on the first screen needs a sideways scroll. The full table with
 * every column lives on /agents.
 */

import { Link } from "react-router";
import { Pill } from "@/components/ui/pill";
import type { Agent } from "@/features/agents/types";
import { ExperimentStatusPill } from "@/features/experiments/status-pill";
import { formatCount, formatRelative, formatScore } from "@/lib/format";

/** "Orchestrator · localhost:8000". */
function whereLabel(agent: Agent): string {
  return `${agent.connector_label} · ${agent.base_url.replace(/^https?:\/\//, "")}`;
}

function LastLine({ agent }: { agent: Agent }) {
  const { last_experiment: last, running } = agent.stats;
  if (running > 0) {
    return (
      <Pill tone="run" dot>
        {running} running
      </Pill>
    );
  }
  if (!last) return <span className="text-xs text-muted">No experiments yet</span>;
  return (
    <span className="flex min-w-0 items-center gap-2 text-xs text-muted">
      <span className="shrink-0">
        <ExperimentStatusPill experiment={last} />
      </span>
      <span className="truncate text-body">{last.name}</span>
      <span className="shrink-0 whitespace-nowrap">· {formatRelative(last.at)}</span>
    </span>
  );
}

function AgentCard({ agent }: { agent: Agent }) {
  const score = agent.stats.last_score;
  return (
    <li>
      <Link
        to={`/agents/${agent.id}`}
        className="box flex h-full flex-col gap-3 px-4 py-3.5 transition-colors hover:border-line hover:bg-hover"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-sm font-semibold" title={agent.name}>
              {agent.name}
            </span>
            <span className="truncate text-xs text-muted" title={agent.base_url}>
              {whereLabel(agent)}
            </span>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-0.5">
            <span className={`font-mono text-xl leading-none ${score == null ? "text-faint" : ""}`}>
              {formatScore(score)}
            </span>
            <span className="text-[11px] text-faint">latest score</span>
          </div>
        </div>
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-line-soft pt-3">
          <LastLine agent={agent} />
          <span className="shrink-0 font-mono text-xs text-muted">
            {formatCount(agent.stats.experiments)} {agent.stats.experiments === 1 ? "run" : "runs"}
          </span>
        </div>
      </Link>
    </li>
  );
}

export function AgentCards({ agents }: { agents: Agent[] }) {
  return (
    <ul aria-label="Agents" className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
      {agents.map((a) => (
        <AgentCard key={a.id} agent={a} />
      ))}
    </ul>
  );
}
